import type { ChatMessage } from './components/ChatPanel';

export interface StoredWidgetSession {
  sessionId: string;
  messages: ChatMessage[];
  isOpen: boolean;
  greetingDismissed: boolean;
  updatedAt: number;
}

function storageKey(publicId: string): string {
  return `audientic_session_${publicId}`;
}

function autoOpenDismissalKey(publicId: string): string {
  return `audientic_autoopen_dismissed_${publicId}`;
}

// How long a dismissed-without-messaging auto-open suppresses re-triggering
// on subsequent page loads within the same visit (#247). Independent of
// sessionPersistenceTtlSeconds — that TTL governs resuming an actual chat,
// this governs not re-annoying a visitor who never chatted at all.
export const AUTO_OPEN_DISMISSAL_COOLDOWN_SECONDS = 1800;

function isStoredWidgetSession(value: unknown): value is StoredWidgetSession {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.sessionId === 'string' &&
    Array.isArray(v.messages) &&
    typeof v.isOpen === 'boolean' &&
    typeof v.greetingDismissed === 'boolean' &&
    typeof v.updatedAt === 'number'
  );
}

// Mirrors isSessionStale (src/services/widget-sessions.ts) — the widget
// bundle can't import from src/services/ to share a single implementation,
// so src/test/staleness-parity.test.ts pins the two to agree on every
// input instead (#144).
export function isStoredSessionStale(updatedAt: number, ttlSeconds: number): boolean {
  return Date.now() - updatedAt > ttlSeconds * 1000;
}

// Reads a persisted session for cross-page resume (#131), discarding it if
// malformed or past the tenant's inactivity TTL (mirrors the server-side TTL
// enforced in #130, so a stale transcript is never shown before the visitor
// even tries to send a message).
export function loadStoredSession(
  publicId: string,
  ttlSeconds: number,
): StoredWidgetSession | null {
  try {
    const raw = window.localStorage.getItem(storageKey(publicId));
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isStoredWidgetSession(parsed)) {
      clearStoredSession(publicId);
      return null;
    }

    if (isStoredSessionStale(parsed.updatedAt, ttlSeconds)) {
      clearStoredSession(publicId);
      return null;
    }

    return parsed;
  } catch (err) {
    console.warn('[Audientic] failed to read persisted session', err);
    clearStoredSession(publicId);
    return null;
  }
}

export function saveStoredSession(publicId: string, session: StoredWidgetSession): void {
  try {
    window.localStorage.setItem(storageKey(publicId), JSON.stringify(session));
  } catch (err) {
    console.warn('[Audientic] failed to persist session', err);
  }
}

export function clearStoredSession(publicId: string): void {
  try {
    window.localStorage.removeItem(storageKey(publicId));
  } catch (err) {
    console.warn('[Audientic] failed to clear persisted session', err);
  }
}

// Records that the visitor closed an auto-opened panel without ever sending
// a message (#247) — the only signal a chat-less visitor leaves behind, so
// auto-open doesn't re-fire on their very next page.
export function saveAutoOpenDismissal(publicId: string): void {
  try {
    window.localStorage.setItem(autoOpenDismissalKey(publicId), String(Date.now()));
  } catch (err) {
    console.warn('[Audientic] failed to persist auto-open dismissal', err);
  }
}

// Returns true when a dismissal was recorded within the cooldown window,
// discarding (and clearing) anything older or malformed.
export function hasRecentAutoOpenDismissal(publicId: string): boolean {
  const key = autoOpenDismissalKey(publicId);
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return false;

    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) {
      window.localStorage.removeItem(key);
      return false;
    }

    if (isStoredSessionStale(dismissedAt, AUTO_OPEN_DISMISSAL_COOLDOWN_SECONDS)) {
      window.localStorage.removeItem(key);
      return false;
    }

    return true;
  } catch (err) {
    console.warn('[Audientic] failed to read auto-open dismissal', err);
    return false;
  }
}
